import { useState, useEffect } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { QRCodeLogin } from '@/components/QRCodeLogin';
import { Mail, Lock, Eye, EyeOff, Loader2, QrCode, ArrowLeft } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().email('Ongeldig e-mailadres');
const passwordSchema = z.string().min(6, 'Wachtwoord moet minimaal 6 tekens zijn');

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'login' | 'signup'; // Default mode when opening modal
  showQROption?: boolean; // Show QR code option for TV/shared devices
  onQRAuthorized?: (teamId: string) => void;
}

export function AuthModal({ isOpen, onClose, defaultMode = 'login', showQROption = true, onQRAuthorized }: AuthModalProps) {
  const { signInWithEmail, signUpWithEmail, signInWithGoogle } = useAuthContext();
  const [mode, setMode] = useState<'login' | 'signup'>(defaultMode);
  const [showQRLogin, setShowQRLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  // Reset mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(defaultMode);
    }
  }, [isOpen, defaultMode]);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      newErrors.email = emailResult.error.errors[0].message;
    }

    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) {
      newErrors.password = passwordResult.error.errors[0].message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    const { error } = mode === 'login'
      ? await signInWithEmail(email, password)
      : await signUpWithEmail(email, password);

    setIsLoading(false);

    if (!error) {
      setEmail('');
      setPassword('');
      setErrors({});
      onClose();
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    await signInWithGoogle();
    // Don't set loading to false - page will redirect
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setErrors({});
  };

  const handleQRAuthorized = (teamId: string) => {
    onQRAuthorized?.(teamId);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowQRLogin(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {showQRLogin ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setShowQRLogin(false)}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                Gedeeld apparaat koppelen
              </div>
            ) : (
              mode === 'login' ? 'Inloggen' : 'Account aanmaken'
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {mode === 'login' ? 'Log in met je account' : 'Maak een nieuw account aan'}
          </DialogDescription>
        </DialogHeader>

        {showQRLogin ? (
          <div className="py-2">
            <QRCodeLogin onAuthorized={handleQRAuthorized} />
          </div>
        ) : (
          <StandardAuthForm
            mode={mode}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            isLoading={isLoading}
            errors={errors}
            setErrors={setErrors}
            handleSubmit={handleSubmit}
            handleGoogleSignIn={handleGoogleSignIn}
            toggleMode={toggleMode}
            showQROption={showQROption}
            onShowQRLogin={() => setShowQRLogin(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface StandardAuthFormProps {
  mode: 'login' | 'signup';
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
  isLoading: boolean;
  errors: { email?: string; password?: string };
  setErrors: (errors: { email?: string; password?: string }) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleGoogleSignIn: () => void;
  toggleMode: () => void;
  showQROption?: boolean;
  onShowQRLogin?: () => void;
}

function StandardAuthForm({
  mode,
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  isLoading,
  errors,
  setErrors,
  handleSubmit,
  handleGoogleSignIn,
  toggleMode,
  showQROption,
  onShowQRLogin,
}: StandardAuthFormProps) {
  return (
    <div className="space-y-4 py-4">
      {/* Google Sign In */}
      <Button
        variant="outline"
        className="w-full"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        )}
        Doorgaan met Google
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            of met e-mail
          </span>
        </div>
      </div>

      {/* Email/Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">E-mail</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="jouw@email.nl"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: undefined });
              }}
              className="pl-10"
              autoComplete="email"
              disabled={isLoading}
            />
          </div>
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Wachtwoord</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors({ ...errors, password: undefined });
              }}
              className="pl-10 pr-10"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {mode === 'login' ? 'Inloggen' : 'Account aanmaken'}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        {mode === 'login' ? (
          <>
            Nog geen account?{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="text-primary hover:underline font-medium"
            >
              Registreren
            </button>
          </>
        ) : (
          <>
            Al een account?{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="text-primary hover:underline font-medium"
            >
              Inloggen
            </button>
          </>
        )}
      </p>

      {/* QR Code option for shared devices */}
      {showQROption && mode === 'login' && (
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                gedeeld apparaat?
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onShowQRLogin}
          >
            <QrCode className="w-4 h-4 mr-2" />
            Koppelen via QR-code
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            Gebruik dit voor TV's en andere gedeelde apparaten
          </p>
        </>
      )}
    </div>
  );
}
